import {RockBottomParsedData} from "./RockBottomParsedData";
import {IBetWayWinData, IBetWinData, IReplayNextPlayerInteraction, IRockBottomGameData} from "./IRockBottomData";
import {RockBottomDataParser} from "./RockBottomDataParser";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {RockBottomGameConfig} from "../../RockBottomGameConfig";
import {RockBottomGameMode} from "../RockBottomGameMode";
import {RockBottom} from "../../RockBottom";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export class RockBottomParsedGameData extends RockBottomParsedData {

    public readonly betWins: IBetWinData[];
    public readonly singleWin: number;
    public readonly playedBetValue: number;
    public readonly wasFeatureBuy: boolean;
    public readonly brokeTheBank: boolean;
    public readonly collectedScatters: number;
    public readonly fsSymbolMultiplier: number[];
    public readonly symbolSizes: number[][];
    public readonly evaluatedArea: string[][];
    public readonly reelsEnteringFreespin: string[][];
    public readonly softWinCapTriggered: boolean;
    public readonly capWinMultiplier: number;
    public readonly fsLevel: number;
    public readonly fsLevelNextSpin: number;
    public readonly enhancedBetPlayed: boolean;
    public readonly isRestoreState: boolean;
    public readonly totalWaysWin: number;
    public readonly playerInteraction: IReplayNextPlayerInteraction;

    // Calculated values
    public readonly currentWinRatioConfig: IWinRatioConfig | null;

    // To add Spin Data
    public readonly nearWinReels: boolean[];
    public readonly nearWinBigWinReels: boolean[];

    // To add Nudge Data
    public readonly nudgeSteps: number[];

    constructor(rawData: IRockBottomGameData, parser: RockBottomDataParser) {
        console.log("GAME DATA", rawData);
        super(rawData, parser);

        this.betWins = parser.parseBetWins(rawData);
        this.singleWin = rawData.totalSpinWinnings;
        this.playedBetValue = rawData.playedBetValue;
        this.wasFeatureBuy = rawData.wasFeatureBuy;
        this.brokeTheBank = rawData.brokeTheBank;
        this.collectedScatters = rawData.collectedScatters;
        this.fsSymbolMultiplier = rawData.fsSymbolMultiplier;
        this.symbolSizes = rawData.symbolSizes;
        this.evaluatedArea = rawData.evaluatedArea;
        this.reelsEnteringFreespin = rawData.reelsEnteringFreespin;
        this.softWinCapTriggered = rawData.softWinCapTriggered;
        this.capWinMultiplier = rawData.capWinMultiplier;
        this.fsLevel = rawData.fsLevel;
        this.fsLevelNextSpin = rawData.fsLevelNextSpin;
        this.enhancedBetPlayed = rawData.enhancedBetPlayed;
        this.isRestoreState = false;
        this.totalWaysWin = this.getTotalWaysWin(rawData.betWayWins);
        this.playerInteraction = rawData.playerInteraction;

        // Calculated values.
        this.currentWinRatioConfig = parser.parseWinRatio(this.singleWin / this.playedBetValue);

        (<IBetWayWinData[]>this.betWins!).forEach((betWins: IBetWayWinData) => {
            betWins.reelWinPositions = this.calculateReelWinPos(rawData, betWins.symbol);
        });

        // To add Spin Data
        this.nearWinReels = parser.parseNearWinReels(rawData, this.scatterPositions);
        this.nearWinBigWinReels = this.getRandomBigWinNearWin();

        // To add Nudge Data
        this.nudgeSteps = parser.parseNudgeSteps(rawData);

        if (this.mode === RockBottomGameMode.WINCAP_PICK && RockBottom.isPortugal()) {
            RockBottom.pickAndClickView.showWinCapMultipliers(rawData);
        }
    }

    private getRandomBigWinNearWin(): boolean[] {
        const values = ArrayHelper.initArrayWithValues(RockBottomGameConfig.REELS_NUM, () => false);

        if(RockBottom.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
            return values;
        }

        values[4] = this.getRandomBigWinTease();

        return values;
    }

    private getRandomBigWinTease(): boolean {
        if (this.wasFeatureBuy) {
            return false;
        }
        switch (this.mode) {
            case RockBottomGameMode.NORMAL:
                return this.currentWinRatioConfig!.ratio + Math.random() * 15 > 35;
            case RockBottomGameMode.FREESPIN:
            case RockBottomGameMode.SUPER_FREESPIN:
                return this.currentWinRatioConfig!.ratio + Math.random() * 20 > 50;
            default:
                return false;
        }
    }

    private getTotalWaysWin(betwayWins: IBetWayWinData[] | undefined): number {
        let totalWaysCount: number = 0;
        if (betwayWins && betwayWins.length > 0) {
            betwayWins.forEach((betWayData: IBetWayWinData) => {
                totalWaysCount += betWayData.numberOfCombinations;
            })
        }
        return totalWaysCount;
    }

    private calculateReelWinPos(rawData: IRockBottomGameData, symbol: string): boolean[][] {
        let evaluatedReelData: string[][] = [];
        if (RockBottomGameConfig.EVALUATED_AREA && RockBottomGameConfig.EVALUATED_AREA.length) {
            evaluatedReelData = RockBottomGameConfig.EVALUATED_AREA;
        } else {
            evaluatedReelData = rawData.evaluatedArea;
        }
        const reelWinPos: boolean[][] = ArrayHelper.initArrayWithValues<boolean[]>(RockBottomGameConfig.REELS_NUM, (index: number) => {
            return ArrayHelper.initArrayWithValues<boolean>(evaluatedReelData[index].length, () => false);
        });
        let connectedReel: boolean = false,
            startingReelId: number = RockBottom.inBonusGame ? 1 : 0

        for (let reelId: number = startingReelId; reelId < evaluatedReelData.length; reelId++) {
            connectedReel = false;
            for (let symId: number = 0; symId < evaluatedReelData[reelId].length; symId++) {
                if (evaluatedReelData[reelId][symId] === symbol ||
                    evaluatedReelData[reelId][symId] === "S" ||
                    evaluatedReelData[reelId][symId] === "W" ||
                    evaluatedReelData[reelId][symId] === "XS" ||
                    (RockBottom.inBonusGame && evaluatedReelData[reelId][symId] === RockBottom.midSymExpand.getActiveSymbolName())) {
                    reelWinPos[reelId][symId] = true;
                    connectedReel = true;
                }
            }
            if (!connectedReel) {
                break;
            }
        }
        return reelWinPos;
    }
}